import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, BRAND } from "@/lib/api";
import PrintHeader from "@/components/PrintHeader";
import LetterheadToggle, { useLetterheadMode } from "@/components/LetterheadToggle";
import { useUserNames } from "@/lib/useUserNames";

const PAYMENT_MODE_LABELS = { cash: "Cash", upi: "UPI", card: "Card", free: "Free", online: "Online", bank: "Bank Transfer", cheque: "Cheque" };
function paymentModeLabel(mode) {
  if (!mode) return "—";
  const key = String(mode).toLowerCase();
  return PAYMENT_MODE_LABELS[key] || String(mode).toUpperCase();
}

/**
 * A5 landscape OPD registration receipt — 210mm x 148mm.
 * Auto-prints once data loads.
 */
export default function PrintRegistrationReceipt() {
  const { invoiceId } = useParams();
  const [inv, setInv] = useState(null);
  const [patient, setPatient] = useState(null);
  const [hospital, setHospital] = useState(null);
  const [branch, setBranch] = useState(null);
  const letterhead = useLetterheadMode();
  const nameOf = useUserNames();

  useEffect(() => {
    (async () => {
      const me = await api.get("/auth/me");
      setHospital(me.data.hospital);
      setBranch(me.data.branch);
      const { data } = await api.get(`/billing/invoices/${invoiceId}`);
      setInv(data);
      if (data?.patient_id) {
        const p = await api.get(`/patients/${data.patient_id}`);
        setPatient(p.data.patient);
      }
      const auto = new URLSearchParams(window.location.search).get("autoprint");
      if (auto !== "0") setTimeout(() => window.print(), 700);
    })();
  }, [invoiceId]);

  if (!inv || !patient) return <div className="p-10 text-sm text-ink-muted">Loading receipt…</div>;
  const item = inv.items?.[0] || {};
  const today = (inv.created_at || "").replace("T", " ").slice(0, 16);

  return (
    <>
      <style>{`@page { size: A5 landscape; margin: 8mm; } body { background: white; }`}</style>
      <div className="receipt-a5-landscape mx-auto bg-white text-[12px] text-ink" style={{ width: "210mm", minHeight: "148mm", padding: "8mm", boxSizing: "border-box" }} data-testid="print-registration-receipt">
        {/* Header */}
        <LetterheadToggle />
        {!letterhead && (
          <PrintHeader
            compact
            hospital={hospital}
            branch={branch}
            subtitle="OPD Registration Receipt"
            right={<>
              <div>Receipt #: <b className="font-mono">{inv.invoice_no}</b></div>
              <div>Date: {today}</div>
            </>}
          />
        )}

        {/* Patient + Doctor */}
        <div className="mt-3 grid grid-cols-2 gap-4">
          <div className="rounded border border-border p-2.5">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-ink-muted">Patient</div>
            <div className="mt-0.5 text-sm font-semibold">{patient.name}</div>
            <div className="mt-1 grid grid-cols-2 gap-x-2 text-[11px]">
              <div><span className="text-ink-muted">UHID</span> <span className="font-mono">{patient.uhid}</span></div>
              <div><span className="text-ink-muted">Mobile</span> {patient.mobile}</div>
              <div><span className="text-ink-muted">Gender / Age</span> {patient.gender}{patient.age ? ` / ${patient.age}` : ""}</div>
              {patient.abha_number && <div><span className="text-ink-muted">ABHA</span> <span className="font-mono">{patient.abha_number}</span></div>}
            </div>
            {patient.address && <div className="mt-1 text-[10px] text-ink-muted">{patient.address}</div>}
          </div>
          <div className="rounded border border-border p-2.5">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-ink-muted">Consultation</div>
            <div className="mt-0.5 text-sm font-semibold">{item.description}</div>
            <div className="mt-2 grid grid-cols-2 gap-x-2 text-[11px]">
              <div><span className="text-ink-muted">Mode</span> <b>{paymentModeLabel(inv.payment_mode)}</b></div>
              <div><span className="text-ink-muted">Status</span> <b className="uppercase">{inv.status}</b></div>
              <div><span className="text-ink-muted">Category</span> <span className="capitalize">{patient.category}</span></div>
              <div><span className="text-ink-muted">Branch</span> {branch?.code || ""}</div>
              {inv.payment_reference && (
                <div className="col-span-2"><span className="text-ink-muted">{(inv.payment_mode || "").toLowerCase() === "card" ? "Card ending" : "Ref / Txn"}</span> <span className="font-mono">{(inv.payment_mode || "").toLowerCase() === "card" ? `xxxx-${inv.payment_reference}` : inv.payment_reference}</span></div>
              )}
            </div>
          </div>
        </div>

        {/* Amount block */}
        <div className="mt-3 flex items-end justify-between rounded-md border border-brand bg-brand/5 p-3">
          <div className="text-[11px] leading-tight text-ink-muted">
            <div>Issued at {hospital?.name || "—"} · {branch?.name || ""}</div>
            <div className="mt-0.5">Please carry this slip for follow-up visits within 7 days.</div>
            <div className="mt-1">Receipt generated by: <b>{nameOf(inv.created_by_email, inv.created_by_email)}</b></div>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-wider text-ink-muted">{inv.status === "free" ? "Free of charge" : "Amount paid"}</div>
            <div className="font-heading text-3xl font-semibold text-brand">
              {inv.status === "free" ? "FREE" : `₹ ${Number(inv.amount_paid).toLocaleString("en-IN")}`}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-4 flex items-end justify-between text-[10px] text-ink-muted">
          <div>This is a system-generated receipt. NABH-compliant medico-legal document.</div>
          <div className="text-right">
            <div className="mt-6 border-t border-ink/40 pt-1 px-6">Authorised Signatory</div>
          </div>
        </div>
        <div className="mt-2 border-t border-border pt-1 text-center text-[9px] text-ink-muted">{BRAND.footer}</div>
      </div>
    </>
  );
}
